import { useEffect, useRef, type ReactNode } from 'react';

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  align?: 'left' | 'right';
  mono?: boolean;
}

/** Selección múltiple opcional (R3.1): la tabla no la conoce, solo la refleja y avisa los cambios. */
export interface TableSelection {
  /** Claves (`rowKey`) seleccionadas de la página actual. */
  selected: string[];
  onChange: (next: string[]) => void;
  /** Texto de la casilla de cada fila; por defecto «Seleccionar {clave}». */
  label?: (rowKey: string) => string;
  /** «Seleccionar los N del filtro»: se ofrece solo si el total viene del servidor. */
  filter?: { total: number; selected: boolean; onChange: (selected: boolean) => void };
}

/**
 * Alto de las listas largas: la caja se queda con su propio scroll y deja a la vista los filtros y la barra
 * de acciones (design-spec §1). El piso de 20rem la mantiene usable en pantallas bajas (teléfono apaisado).
 */
export const LIST_MAX_HEIGHT = 'max(20rem, calc(100dvh - 24rem))';

export interface TableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  caption?: string;
  loading?: boolean;
  empty?: ReactNode;
  /** Casilla por fila y en el encabezado. Sin esta prop la tabla se comporta como siempre. */
  selection?: TableSelection;
  /** Encabezado de columnas pegado al tope de la caja (design-spec §1). */
  stickyHeader?: boolean;
  /** Alto máximo de la caja con scroll propio, en CSS (ej. `calc(100vh - 22rem)`). */
  maxHeight?: string;
}

function Checkbox({ checked, indeterminate = false, onChange, label }: { checked: boolean; indeterminate?: boolean; onChange: () => void; label: string }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      aria-label={label}
      className="h-[18px] w-[18px] cursor-pointer accent-green"
    />
  );
}

export function Table<T>({ columns, rows, rowKey, caption, loading = false, empty, selection, stickyHeader = false, maxHeight }: TableProps<T>) {
  const pageKeys = rows.map(rowKey);
  const chosen = new Set(selection?.selected ?? []);
  const allFilter = selection?.filter?.selected ?? false;
  const isSelected = (key: string) => allFilter || chosen.has(key);
  const count = allFilter ? (selection?.filter?.total ?? pageKeys.length) : chosen.size;
  const pageAll = pageKeys.length > 0 && pageKeys.every((k) => isSelected(k));
  const span = columns.length + (selection ? 1 : 0);

  function selectPage() {
    selection?.filter?.onChange(false);
    selection?.onChange([...new Set([...chosen, ...pageKeys])]);
  }

  function clear() {
    selection?.filter?.onChange(false);
    selection?.onChange([]);
  }

  function toggleRow(key: string) {
    if (!selection) return;
    if (allFilter) {
      selection.filter?.onChange(false);
      selection.onChange(pageKeys.filter((k) => k !== key));
      return;
    }
    selection.onChange(chosen.has(key) ? selection.selected.filter((k) => k !== key) : [...selection.selected, key]);
  }

  const headerCell = stickyHeader ? 'sticky top-0 z-[1] bg-white' : '';

  return (
    <div className="rounded-lg border border-line bg-white">
      {selection && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line px-4 py-2 text-xs text-ink-muted">
          <span className="font-semibold tabular-nums text-ink" aria-live="polite">
            {count === 1 ? '1 seleccionado' : `${count} seleccionados`}
          </span>
          <span>
            Seleccionar{' '}
            <button type="button" onClick={selectPage} className="font-medium text-green underline">
              la página
            </button>
            {selection.filter && (
              <>
                {' · '}
                <button type="button" onClick={() => selection.filter?.onChange(true)} className="font-medium text-green underline">
                  los {selection.filter.total.toLocaleString('es-CL')} del filtro
                </button>
              </>
            )}
          </span>
          {count > 0 && (
            <button type="button" onClick={clear} className="font-medium text-green underline">
              Limpiar selección
            </button>
          )}
        </div>
      )}
      <div className="overflow-x-auto overscroll-contain" style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}>
        <table className="w-full text-sm">
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead className="text-xs text-ink-muted">
            <tr>
              {selection && (
                <th scope="col" className={`w-10 px-4 py-2 font-medium ${headerCell}`}>
                  <Checkbox checked={pageAll} indeterminate={count > 0} onChange={() => (pageAll ? clear() : selectPage())} label="Seleccionar toda la página" />
                </th>
              )}
              {columns.map((c) => (
                <th key={c.key} scope="col" className={`px-4 py-2 font-medium ${c.align === 'right' ? 'text-right' : 'text-left'} ${headerCell}`}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading &&
              Array.from({ length: 3 }).map((_, i) => (
                <tr key={`sk-${i}`} className="border-t border-line">
                  <td colSpan={span} className="px-4 py-3">
                    <div className="h-4 w-full animate-pulse rounded bg-gray-100" />
                  </td>
                </tr>
              ))}
            {!loading && rows.length === 0 && (
              <tr className="border-t border-line">
                <td colSpan={span} className="px-4 py-8 text-center text-ink-muted">
                  {empty ?? 'Sin resultados.'}
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((row) => {
                const key = rowKey(row);
                const selected = Boolean(selection) && isSelected(key);
                return (
                  <tr key={key} className={`border-t border-line ${selected ? 'bg-yellow-soft' : 'hover:bg-gray-50'}`}>
                    {selection && (
                      <td className="px-4 py-2">
                        <Checkbox checked={selected} onChange={() => toggleRow(key)} label={selection.label?.(key) ?? `Seleccionar ${key}`} />
                      </td>
                    )}
                    {columns.map((c) => (
                      <td key={c.key} className={`px-4 py-2 ${c.align === 'right' ? 'text-right' : ''} ${c.mono ? 'font-mono' : ''}`}>
                        {c.render(row)}
                      </td>
                    ))}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
